import { createRouter, createWebHistory } from 'vue-router'
import { isAdminUser } from '@/composables/useOrgUsers'
import { useUserStore } from '../store/userStore'
import { useProjectsStore } from '../store/projectStore'
import { trackPageView, setSentryUser } from '@/observability/sentry'
// FLY3D-914: new config-driven Settings dashboard (children generated from
// the SETTINGS_SECTIONS array — see views/Settings/settingsSections.ts).
import {
  buildSettingsChildRoutes,
  DEFAULT_SETTINGS_ROUTE,
  settingsRouteName,
} from '@/views/Settings/settingsSections'

// Lazy load all route components for better code splitting
const MissionPanning = () => import('../views/MissionPlanning.vue')
const OverviewComponent = () => import('../views/Profile/Overview.vue')
const SettingsLayout = () => import('../layouts/SettingsLayout.vue')
// FLY3D-914 Phase 12: chromeless special pages (rendered outside the Settings
// shell). Route names match settingsRouteName(route) so the dashboard's
// "States & Pages" nav can link to them by name.
const PublicPinPage = () => import('../views/Settings/special/PublicPinPage.vue')
const AccessRevokedPage = () => import('../views/Settings/special/AccessRevokedPage.vue')
const FlyDashboard = () => import('../views/FlyDashboard.vue')
const MapLayout = () => import('../layouts/MapLayout.vue')
const Analytics = () => import('@/views/Analytics.vue')
const TriggerTest = () => import('@/views/TriggerTest.vue')
const Login = () => import('../views/login.vue')
// FLY3D-938: project-first entry — full-screen gate shown before the main app.
const ProjectSelect = () => import('../views/ProjectSelect.vue')
const Verify = () => import('../views/verification.vue')
const twoFactorAuth = () => import('../views/setup/authentication.vue')
const CreatePassword = () => import('../views/setup/createPassword.vue')
const DeviceManagement = () => import('../views/setup/deviceManagement.vue')
const test = () => import('../views/setup/test.vue')
const UserManagement = () => import('../views/setup/userManagement.vue')
const profile = () => import('../views/invitedMemberSetup/profile.vue')
const Eula = () => import('../views/setup/eula.vue')
const ChangePassword = () => import('@/views/Profile/ChangePassword.vue')
const MediaManagement = () => import('../views/MediaManagement.vue')
const FlightStory = () => import('../views/FlightStory.vue')
const Ticket = () => import('../views/ticket.vue')
const TourPage = () => import('../views/onboarding/TourPage.vue')
const DeviceOnboarding = () => import('../views/onboarding/DeviceOnboarding.vue')
const OnboardingDev = () => import('../views/onboarding/OnboardingDev.vue')
const SetupComplete = () => import('../views/setup/setupComplete.vue')
import admin from '@/views/admin.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: OverviewComponent,
      meta: { requiresAuth: true, requiresProject: true },
    },
    // FLY3D-938: the project selection gate. Top-level and chromeless (like
    // /login) — every requiresProject route bounces here until a project is
    // active, so there is always a selected project inside the app.
    {
      path: '/projects',
      name: 'ProjectSelect',
      component: ProjectSelect,
      meta: { requiresAuth: true },
    },
    {
      path: '/trigger-test',
      name: 'TriggerTest',
      component: TriggerTest,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    // FLY3D-914: config-driven Settings dashboard at /settings. Child routes are
    // generated from the SETTINGS_SECTIONS config so adding a screen is a
    // one-line change. The parent redirects to the first subsection.
    {
      path: '/settings',
      name: 'Settings',
      component: SettingsLayout,
      meta: { requiresAuth: true, requiresProject: true },
      redirect: { name: `Settings.${DEFAULT_SETTINGS_ROUTE}` },
      children: buildSettingsChildRoutes(),
    },
    // FLY3D-914 Phase 12: chromeless special pages, mounted top-level (not under
    // the /settings shell). Public PIN is anonymous (viewer-link redemption);
    // Access Revoked is reached via a live revocation push for a signed-in user.
    {
      path: '/share/pin/:token?',
      name: settingsRouteName('public-pin'),
      component: PublicPinPage,
    },
    {
      path: '/share/revoked',
      name: settingsRouteName('access-revoked'),
      component: AccessRevokedPage,
      meta: { requiresAuth: true },
    },
    {
      path: '/',
      name: 'MapLayout',
      component: MapLayout,
      meta: { requiresAuth: true, requiresProject: true },
      children: [
        {
          path: '/mission-plan',
          name: 'MissionPlan',
          component: MissionPanning,
        },
        {
          path: '/fly-dashboard',
          name: 'FlyDashboard',
          component: FlyDashboard,
        },
        {
          path: '/analytics',
          name: 'Analytics',
          component: Analytics,
        },
        {
          path: '/flight-story',
          name: 'FlightStory',
          component: FlightStory,
        },
      ],
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
      meta: { requiresGuest: true },
    },
    {
      path: '/media-management',
      name: 'MediaManagement',
      component: MediaManagement,
      meta: { requiresAuth: true, requiresProject: true },
    },
    {
      path: '/admin',
      name: 'Admin',
      component: admin,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/change-password/:username?',
      name: 'ChangePassword',
      component: ChangePassword,
    },
    {
      path: '/verification/:method',
      name: 'verify',
      component: Verify,
      meta: { requiresGuest: false },
    },
    {
      path: '/setup/create-password',
      name: 'CreatePassword',
      component: CreatePassword,
      meta: { requiresGuest: true },
    },
    {
      path: '/setup/two-factor-auth/:dashboard?',
      name: 'twoFactorAuth',
      component: twoFactorAuth,
      // meta: { requiresGuest: true }
    },
    {
      path: '/setup/device-management/:dashboard?',
      name: 'DeviceManagement',
      component: DeviceManagement,
      // requiresProject: the device list is project-scoped (FLY3D-938), so a
      // deep link without an active project must pass the gate first.
      meta: { requiresAuth: true, requiresProject: true },
    },
    {
      path: '/setup/user-management/:dashboard?',
      name: 'UserManagement',
      component: UserManagement,
      // requiresProject: the device-group column and the "Add User to Group"
      // picker are project-scoped (FLY3D-938).
      meta: { requiresAuth: true, requiresProject: true },
    },
    // FLY3D-590 Phase B: "You're all set" hand-off after invited-user
    // setup (eula → profile → password → 2FA). Registered BEFORE
    // `/setup/:token` so the dynamic segment can't eat it.
    {
      path: '/setup/complete',
      name: 'SetupComplete',
      component: SetupComplete,
      meta: { requiresAuth: true },
    },
    // ── Onboarding (FLY3D-590) ─────────────────────────────────────
    // Registered BEFORE `/setup/:token` so the dynamic segment can't
    // eat them. Phase A only registers the routes; Phases C and E
    // fill in the views.
    {
      path: '/onboarding/tour',
      name: 'OnboardingTour',
      component: TourPage,
      meta: { requiresAuth: true },
    },
    {
      path: '/onboarding/device/:kind(dock)',
      name: 'OnboardingDevice',
      component: DeviceOnboarding,
      // requiresProject: finishing the wizard assigns the dock to the active
      // project (FLY3D-938), so there must be one.
      meta: { requiresAuth: true, requiresProject: true },
    },
    // Dev-only mounting page for FLY3D-590 Phase A primitives.
    // Stripped from production bundles via `import.meta.env.DEV` so
    // the route only registers when Vite is in dev mode.
    ...(import.meta.env.DEV
      ? [
          {
            path: '/onboarding/dev',
            name: 'OnboardingDev',
            component: OnboardingDev,
            meta: { requiresAuth: true },
          },
        ]
      : []),
    {
      path: '/setup/:token',
      name: 'eula',
      component: Eula,
      meta: { requiresAuth: false, requiresGuest: true },
    },
    {
      path: '/setup/profile/:token',
      name: 'profile',
      component: profile,
      meta: { requiresAuth: false, requiresGuest: true },
    },
    {
      path: '/test',
      name: 'test',
      component: test,
      meta: { requiresAuth: true },
    },
    {
      path: '/ticket',
      name: 'ticket',
      component: Ticket,
      meta: { requiresAuth: true },
    },
  ],
})
router.beforeEach((to, from, next) => {
  const authStore = useUserStore()

  // Check if user is authenticated
  if (to.meta.requiresAuth && !authStore.isLoggedIn) {
    next({ name: 'Login' })
    return
  }

  // FLY3D-938: project-first entry — project-scoped routes need an active
  // project. Until one is picked (or auto-picked when only Default exists),
  // everything funnels through the Project Selection gate.
  if (
    authStore.isLoggedIn &&
    to.matched.some((record) => record.meta.requiresProject) &&
    !useProjectsStore().activeProject
  ) {
    next({ name: 'ProjectSelect' })
    return
  }

  // Check if route requires admin access
  if (to.meta.requiresAdmin && authStore.isLoggedIn) {
    if (!isAdminUser()) {
      next({ name: 'home' })
      return
    }
  }

  // Check if guest-only route
  if (to.meta.requiresGuest && authStore.isLoggedIn) {
    next({ name: 'home' })
    return
  }

  next()
})

// Track page views and keep Sentry user identity fresh on every navigation
router.afterEach((to) => {
  const pageName = String(to.name || to.path)
  trackPageView(pageName, to.fullPath)

  // Refresh Sentry user context so errors on any page carry the current identity
  const authStore = useUserStore()
  if (authStore.isLoggedIn && authStore.userId) {
    const details = authStore.userDetails as Record<string, string> | null
    setSentryUser({
      id: authStore.userId,
      username: details?.['cognito:username'] || authStore.userId,
      email: details?.['email'],
      organisation: authStore.organisationId || undefined,
    })
  }
})

export default router
